// Os alertas da Central do Perito (fase B): o prazo vencido ou perto de vencer, a tarefa atrasada e o
// honorário vencido. Portados de `useAlertasComputados` (`src/lib/store.ts` de lá) em 03/11, com quatro
// diferenças, todas defeitos de lá:
//
//  - o dia é o de São Paulo (`datas.ts`). Lá a conta era `Math.ceil((meia-noite UTC do prazo − agora) / dia)`,
//    então o prazo de hoje virava "vencido há 0 dia(s)" depois das 21h e "vence em 0d" antes;
//  - o prazo de hoje diz "vence hoje", e não "vence em 0d";
//  - o valor do honorário sai em reais ("R$ 1.500,00"), e não com ponto ("R$ 1500.00");
//  - o valor é o que FALTA receber. Lá era o valor total, e o honorário recebido em parte alertava pelo total.
//
// É uma função PURA (a tela passa o estado e o relógio), para a prova pura medir as fronteiras sem banco.
import { diaDe, diasAte } from "./datas";
import { formatBRL } from "./format";
import type { NivelAlerta, Pericia, Prazo, Tarefa } from "./tipos";

export interface Alerta {
  nivel: NivelAlerta;
  titulo: string;
  detalhe?: string;
  pericia_id?: string;
  /** O dia a que o alerta se refere (AAAA-MM-DD): é ele que ordena a lista. */
  dia: string;
  /** A identidade do alerta (o tipo e o id da origem): a chave da lista e o gancho da prova. */
  chave: string;
}

const diasEmTexto = (n: number) => `${n} ${n === 1 ? "dia" : "dias"}`;

/**
 * Os níveis, como lá: o prazo vencido e o que vence em até 3 dias são CRÍTICOS; o prazo de 4 a 7 dias, a tarefa
 * atrasada e o honorário vencido são de ATENÇÃO. O prazo cumprido, a tarefa concluída e o honorário pago não
 * alertam. A lista vem do dia mais antigo para o mais novo: o que venceu primeiro aparece primeiro.
 */
export function calcularAlertas(pericias: Pericia[], tarefas: Tarefa[], prazos: Prazo[], agora: Date = new Date()): Alerta[] {
  const lista: Alerta[] = [];

  for (const p of prazos) {
    if (p.cumprido) continue;
    const dias = diasAte(p.data, agora);
    const dia = diaDe(p.data);
    if (dias === null || !dia) continue;
    const base = { pericia_id: p.pericia_id, dia, chave: `prazo:${p.id}` };
    if (dias < 0) lista.push({ ...base, nivel: "critico", titulo: `Prazo vencido: ${p.titulo}`, detalhe: `há ${diasEmTexto(-dias)}` });
    else if (dias === 0) lista.push({ ...base, nivel: "critico", titulo: "Prazo vence hoje", detalhe: p.titulo });
    else if (dias <= 3) lista.push({ ...base, nivel: "critico", titulo: `Prazo vence em ${diasEmTexto(dias)}`, detalhe: p.titulo });
    else if (dias <= 7) lista.push({ ...base, nivel: "atencao", titulo: `Prazo em ${diasEmTexto(dias)}`, detalhe: p.titulo });
  }

  for (const t of tarefas) {
    if (t.status === "concluida" || !t.prazo) continue;
    const dias = diasAte(t.prazo, agora);
    const dia = diaDe(t.prazo);
    if (dias === null || !dia || dias >= 0) continue;
    lista.push({ nivel: "atencao", titulo: `Tarefa atrasada: ${t.titulo}`, detalhe: `há ${diasEmTexto(-dias)}`, pericia_id: t.pericia_id, dia, chave: `tarefa:${t.id}` });
  }

  for (const p of pericias) {
    for (const h of p.honorarios) {
      if (h.status === "pago" || !h.vencimento) continue;
      const dias = diasAte(h.vencimento, agora);
      const dia = diaDe(h.vencimento);
      if (dias === null || !dia || dias >= 0) continue;
      lista.push({ nivel: "atencao", titulo: "Honorário vencido", detalhe: `${h.descricao} — ${formatBRL(h.valor - h.recebido)} a receber`, pericia_id: p.id, dia, chave: `honorario:${h.id}` });
    }
  }

  return lista.sort((a, b) => a.dia.localeCompare(b.dia) || a.chave.localeCompare(b.chave));
}

/** Quantos alertas pedem atenção (os críticos e os de atenção): é o número que a navegação mostra. */
export function quantosPedemAtencao(alertas: Alerta[]): number {
  return alertas.filter((a) => a.nivel === "critico" || a.nivel === "atencao").length;
}
